import { Credits } from "./credits";
import { Z2DataMatch } from "./dataProviders/z2data";
import {
  EuRoHSStatus,
  ObsolescencelifeCycleRisk,
  ObsolescenceStatus,
  ReachAffectedStatus,
  WITHOUT_DATA
} from "./rm-common-properties";

export type MineralStatus =
  | ""
  | "drc"
  | "ndrc"
  | "partialUndeterminable"
  | "undeterminable"
  | "unknown";

export type Multi<T> = T | T[];

export class PcnData {
  [key: string]: any;
  pcnIssueDate?: string = "";
  pcnDescription?: string = "";
  pcnTypeOfChange?: string = "";
  pcnID?: string = "";
  pcnSource?: string = "";
  pcnEffectiveDate?: string = "";
}

export class Inventory {
  buyNowLink: string = "";
  distributor: string = "";
  distributorType: string = "";
  lastUpdated: string = "";
  quantity: string = "";
}

export class ManufacturerData extends Credits {
  name: string = "";
  nameRaw?: string = "";
  nameClean?: string = "";
  code?: string = "";
  partNumber: string = "";
  manufacturerPartNumber: string = "";
  manufacturerPartNumberRaw?: string = "";
  manufacturerPartNumberClean?: string = "";
  manufacturerPartDescription: string = "";
  manufacturerPartDescriptionRaw?: string = "";
  manufacturerPartDescriptionClean?: string = "";
  MPNAssembly?: string = "";
  purchasePartNumber?: string = "";
  active: boolean = false;
  valid: boolean = true;
  obsolescenceDate?: number = 0;
  yearsToEndOfLife?: string = "";
  startOfProduction?: number = 0;
  endOfProduction?: string = "";
  endOfSale?: string = "";
  seId?: string = "";
  z2DataId?: string = "";
  matches?: Z2DataMatch[] = [];
  edited?: boolean = false;
  numberOfMatches?: string = "";
  estimatedYearsToEOL?: string = "";
  estimatedEOLDate?: number = 0;
  lifeCycleRisk?: string = "";
  ltbDate?: number = 0;
  cage?: string = "";
  risk?: string = "";
  euRoHSStatus?: string = "";
  euRoHSVersion?: string = "";
  eolLink?: string = "";
  pcnHistory?: string = "";
  lastPCNDate?: string = "";
  lastPCNNumber?: string = "";
  lastEOLDate?: string = "";
  searchResults?: boolean = false;
  package?: string = "";
  packageDiameter?: string = "";
  packageLength?: string = "";
  packageWidth?: string = "";
  packageHeight?: string = "";
  encryptionStandard?: string = "";
  sources?: string = "";
  taxonomy?: string = "";
  lifecycleCode?: string = "";
  conformityCertificate?: string = "";
  datasheet?: string = "";
  lastCheckDate?: number = 0;
  seStatus?: string = "";
  oldSeStatus?: string = "";
  z2DataStatus?: string = "";
  oldZ2DataStatus?: string = "";
  eccn?: string = "";
  minTemp?: string = "";
  maxTemp?: string = "";
  minStorageTemp?: string = "";
  maxStorageTemp?: string = "";
  msl?: string = "";
  crossType?: string = "";
  differences?: string = "";
  replacementType?: string = "";
  conflictMineralStatus?: string = "";
  conflictMineralStatement?: string = "";
  chinaRoHSStatus?: string = "";
  chinaRoHSDeclaration?: string = "";
  tscaStatus?: string = "";
  tscaSource?: string = "";
  tscaDeclaration?: string = "";
  image?: string = "";
  productEOSR?: number = 0;
  productYearsToEOSR?: string = "";
  likelihood_yteol?: string = "";
  likelihood_yteosr?: string = "";
  countryOfOrigin?: string = "";
  manufacturingLocations?: ManufacturingLocation[] = [];
  customsTariff?: string = "";
  reach?: string = "";
  /**List of Substances of Very High Concern */
  svhcItems?: SVHCItem[] = [];
  svhcListVersion?: string = "";
  scipNumber?: string = "";
  materialDeclaration?: string = "";
  environmentalLink?: string = "";
  averagePrice?: string = "";
  net_price?: number | null = null;
  procurement_category?: string = "";
  net_price_with_surcharge?: number | null = null;
  applied_factor?: number | null = null;
  stockOnHand?: string = "";
  demand?: string = "";
  onOrder?: string = "";
  pcnNotificationDate?: number = 0;
  lastUpdatedOn?: number = 0;
  maxWeeks?: string = "";
  minWeeks?: string = "";
  partner?: string = "";
  exemption?: string = "";
  expirationDate?: number = 0;
  designType?: string = "";
  sapStatus?: string = "";
  stockRange?: number = -1;
  euRoHS?: EuRoHSStatus = "";
  obsolescenceStatus?: ObsolescenceStatus = WITHOUT_DATA;
  obsolescenceStatusOverride: boolean = false;
  obsolescenceStatus2years?: ObsolescenceStatus = WITHOUT_DATA;
  obsolescenceStatus4years?: ObsolescenceStatus = WITHOUT_DATA;
  obsolescenceStatus6years?: ObsolescenceStatus = WITHOUT_DATA;
  obsolescenceStatus8years?: ObsolescenceStatus = WITHOUT_DATA;
  likelihood?: ObsolescencelifeCycleRisk = "";
  impact?: ObsolescencelifeCycleRisk = "";
  totalRisk?: ObsolescencelifeCycleRisk = "";
  reachStatus?: string = "";
  reachSubstances?: string = "";
  reachAffected?: ReachAffectedStatus = "";
  leadTime?: ObsolescencelifeCycleRisk = "";
  materialName?: string = "";
  materialClass?: string = "";
  supplierPending?: boolean = false;
  instructions?: string = "";
  pfasStatus?: string = "";
  pfasDeclaration?: string = "";
  euPOPs?: string = "";
  euPOPsDeclaration?: string = "";
  californiaStatus?: string = "";
  californiaSDeclaration?: string = "";
  mtbf?: number | null = null;
  mtbf_z2?: number | null = null;
  distributor?: string = "";
  quantity?: string = "";
  lastUpdated?: string = "";
  buyNowLink?: string = "";
  htsusa?: string = "";
  numberOfDistributors: string = "";
  totalMarketInv?: string = "";
  lastOrderDate?: number = 0;
  partObsolescenceStatus?: ObsolescenceStatus = "";
  partObsolescenceManagement?: boolean = false;
  verwa?: string = "";
  netWeight?: string = "";
  location?: string = "";
  matchReview?: boolean = false;
  site?: string[] = [];
  automotiveQualified?: string = "";
  automotiveStandards?: string = "";
  shelfLifeMonths?: string = "";
  shelfLifeStart?: string = "";
  dateOfDelivery?: string = "";
  dateOfManufacture?: string = "";
  dateOfAssembly?: string = "";
  dateOfShipment?: string = "";
  shelfLifeCondition?: string = "";
}

export type ManufacturingLocation = {
  CompanyLocationID: number;
  Facilitytype: string;
  TrustLevel: string;
  CountryName: string;
  StateProvinceName: string;
  CityName: string;
  SiteOwner: string;
  DisplayName: string;
  Address: string;
  DocId?: string;
};

/** SVHC =  Substance of Very High Concern */
export class SVHCItem {
  [key: string]: any;
  svhcListVersion?: string = "";
  substanceIdentification?: string = "";
  casNumber?: string = "";
  concentration?: string = "";
  thresholdLimitExceeded?: boolean = false;
}

export class Manufacturer extends ManufacturerData {
  [key: string]: any;
  _id?: string = "";
  pcnIssueDate?: number = 0;
  pcnDescription?: string = "";
  pcnTypeOfChange?: string = "";
  pcnID?: string = "";
  pcnSource?: string = "";
  pcnEffectiveDate?: number = 0;
  // type: string = "";
}

//Class used in the matched manufacturers list request
export class MatchedManufacturer {
  manufacturerPartNumber = "";
  name = "";
}
